import { useCallback, useState } from 'react'

import { CellClickModifiers } from './types'

function uniqueRows(rows: number[]): number[] {
  return Array.from(new Set(rows)).sort((a, b) => a - b)
}

export function useRowSelection(): {
  selectedRows: number[]
  setSelectedRows: (rows: number[]) => void
  resetSelection: () => void
  toggleRowSelection: (rowIndex: number, checked: boolean, shiftKeyPressed: boolean) => void
  selectRowByClick: (rowIndex: number, modifiers: CellClickModifiers) => void
} {
  const [selectedRows, setSelectedRowsState] = useState<number[]>([])
  const [lastSelectedRow, setLastSelectedRow] = useState<number | null>(null)

  const setSelectedRows = useCallback((rows: number[]) => {
    setSelectedRowsState(uniqueRows(rows))
    setLastSelectedRow(rows.length > 0 ? rows[rows.length - 1] : null)
  }, [])

  const resetSelection = useCallback(() => {
    setSelectedRowsState([])
    setLastSelectedRow(null)
  }, [])

  const toggleRowSelection = useCallback(
    (rowIndex: number, checked: boolean, shiftKeyPressed: boolean) => {
      if (!checked) {
        setSelectedRowsState((prev) => prev.filter((row) => row !== rowIndex))
        setLastSelectedRow(rowIndex)
        return
      }

      if (shiftKeyPressed && lastSelectedRow !== null) {
        const from = Math.min(lastSelectedRow, rowIndex)
        const to = Math.max(lastSelectedRow, rowIndex)
        const range = Array.from({ length: to - from + 1 }, (_, i) => from + i)
        setSelectedRowsState((prev) => uniqueRows([...prev, ...range]))
        setLastSelectedRow(rowIndex)
        return
      }

      setSelectedRowsState((prev) => uniqueRows([...prev, rowIndex]))
      setLastSelectedRow(rowIndex)
    },
    [lastSelectedRow]
  )

  // A plain click on a row that is already selected keeps the selection: a number typed into a
  // cell writes to every selected row, so the click that puts the caret there must not undo it.
  const selectRowByClick = (rowIndex: number, { extendRange, addToSelection }: CellClickModifiers): void => {
    if (extendRange || addToSelection || selectedRows.includes(rowIndex)) {
      toggleRowSelection(rowIndex, true, extendRange)
      return
    }

    setSelectedRows([rowIndex])
  }

  return {
    selectedRows,
    setSelectedRows,
    resetSelection,
    toggleRowSelection,
    selectRowByClick,
  }
}
